<template>
  <div
    v-if="data.showPage"
    class="i-coaching-upsell"
  >
    <!-- Scarcity Strip -->
    <iScarcityStrip />

    <!-- Sticky Nav -->
    <!-- <iStickyNav :high-pricing="highPricing" :pricing="pricing" @book="scrollToBook" /> -->

    <!-- Hero -->
    <iHeroSection
      :high-pricing="highPricing"
      :pricing="pricing"
      :timing="timing"
      :coach-name="assignedCoachFirstName"
      :coach-picture="assignedCoachProfilePicture"
      @book="scrollToBook"
    />

    <!-- Stats Bar -->
    <iStatsBar />

    <!-- What Is / Isn't -->
    <iWhatIsIsnt
      :high-pricing="highPricing"
      :pricing="pricing"
      :timing="timing"
    />

    <!-- How It Works (30-min timeline + CTA banner) -->
    <iHowItWorks
      :high-pricing="highPricing"
      :pricing="pricing"
      :timing="timing"
      :payment-processing="paymentProcessing"
      @skip="goToHome"
      @book="scrollToBook"
    />

    <!-- Sample Output (tabbed plan cards + inline CTA) -->
    <iSampleOutput
      :pricing="pricing"
      :timing="timing"
      :payment-processing="paymentProcessing"
      @skip="goToHome"
      @book="scrollToBook"
    />

    <!-- ROI Math -->
    <iRoiSection
      :pricing="pricing"
      :payment-processing="paymentProcessing"
      @skip="goToHome"
      @book="scrollToBook"
    />

    <!-- Coaches -->
    <iCoachesSection :timing="timing" />

    <!-- Specific Outputs grid -->
    <iSpecificOutputs />

    <!-- Testimonials + reviews note + inline CTA -->
    <iTestimonialsSection
      :pricing="pricing"
      :timing="timing"
      :payment-processing="paymentProcessing"
      @skip="goToHome"
      @book="scrollToBook"
    />

    <!-- Pricing card -->
    <iPricingSection
      :high-pricing="highPricing"
      :pricing="pricing"
      :timing="timing"
      :payment-processing="paymentProcessing"
      @skip="goToHome"
      @book="handleBook"
    />

    <!-- FAQ -->
    <iFaqSection
      :pricing="pricing"
      :timing="timing"
    />

    <!-- Final CTA -->
    <iFinalCta
      :high-pricing="highPricing"
      :pricing="pricing"
      :timing="timing"
      :payment-processing="paymentProcessing"
      @skip="goToHome"
      @book="scrollToBook"
    />

    <!-- Footer -->
    <iCoachingUpsellFooter />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue"
import router from "@/router"
import { useCoachingStore } from "@/stores/coaching"
import { useUserStore } from "@/stores/user"

import { GlobalVariables } from "@/helpers/globalVariables"
import api from "@/services/apiClient"
import ErrorService from "@/services/errorService"

import iScarcityStrip from "@/components/report/coachingUpsell/ScarcityStrip.vue"
// import iStickyNav from "@/components/report/coachingUpsell/StickyNav.vue"
import iHeroSection from "@/components/report/coachingUpsell/HeroSection.vue"
import iStatsBar from "@/components/report/coachingUpsell/StatsBar.vue"
import iWhatIsIsnt from "@/components/report/coachingUpsell/WhatIsIsnt.vue"
import iHowItWorks from "@/components/report/coachingUpsell/HowItWorks.vue"
import iSampleOutput from "@/components/report/coachingUpsell/SampleOutput.vue"
import iRoiSection from "@/components/report/coachingUpsell/RoiSection.vue"
import iCoachesSection from "@/components/report/coachingUpsell/CoachesSection.vue"
import iSpecificOutputs from "@/components/report/coachingUpsell/SpecificOutputs.vue"
import iTestimonialsSection from "@/components/report/coachingUpsell/TestimonialsSection.vue"
import iPricingSection from "@/components/report/coachingUpsell/PricingSection.vue"
import iFaqSection from "@/components/report/coachingUpsell/FaqSection.vue"
import iFinalCta from "@/components/report/coachingUpsell/FinalCta.vue"
import iCoachingUpsellFooter from "@/components/report/coachingUpsell/CoachingUpsellFooter.vue"

// TYPES
import ApiClient, { ApiError } from "@/shared/apiClient"
import StripePrice from "@/shared/enums/StripePrice"
import { CoachAssigned } from "@/shared/types/Coach"
import StripePostInvoiceChargeOut from "@/shared/dtos/out/StripePostInvoiceChargeOut"

// STORES
const coachingStore = useCoachingStore()
const userStore = useUserStore()

// CONSTANTS
const STRIPE_IDEMPOTENCY_KEY = ApiClient.generateIdempotencyKey()

// DATA
const data = reactive({
  assignedCoach: {} as CoachAssigned,
  showPage: false
})

// REFS
const paymentProcessing = ref(false)

// COMPUTED
const assignedCoachFirstName = computed(() => data.assignedCoach?.fullName?.split(" ")[0])
const assignedCoachProfilePicture = computed(() => data.assignedCoach?.profilePhotoUrl || "")
const highPricing = computed(() => (userStore.isExecutiveCoachingPotential ? "300.00" : "149.90"))
const pricing = computed(() => (userStore.isExecutiveCoachingPotential ? "67" : "49"))
const timing = computed(() => (userStore.isExecutiveCoachingPotential ? "60" : "30"))
const stripePrice = computed(() =>
  userStore.isExecutiveCoachingPotential ? StripePrice.COACHING_DISCOVERY_LOW : StripePrice.COACHING_DISCOVERY_ONE
)

// FUNCTIONS
const scrollToBook = () => {
  document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" })
}

const handleBook = async () => {
  if (!paymentProcessing.value) {
    paymentProcessing.value = true
    try {
      const result: StripePostInvoiceChargeOut = await api.stripe.postInvoiceCharge({
        stripePrices: [stripePrice.value],
        idempotencyKey: STRIPE_IDEMPOTENCY_KEY
      })

      router.push({
        name: GlobalVariables.urls.coaching.children.confirmed.routeName,
        query: { val: pricing.value, item: stripePrice.value, invoiceId: result.invoiceId }
      })
    } catch (error) {
      ErrorService.redirect(error as ApiError)
    } finally {
      paymentProcessing.value = false
    }
  }
}

const assignJulieAsCoach = async () => {
  const userId = userStore.getUserId

  if (userId) {
    try {
      const response = await api.coach.postAssigned({ userId, coachId: import.meta.env.VITE_ENV === "prod" ? 22 : 18 }) // Julie in prod
      data.assignedCoach = response.coach
      coachingStore.setAssignedCoach(data.assignedCoach)
    } catch (error) {
      console.error("Error assigning user to coach Julie", error)
      router.push({ name: GlobalVariables.urls.report.children.home.routeName })
    }
  }
}

const goToHome = async () => {
  await userStore.loadUserData()
  router.push({ name: GlobalVariables.urls.report.children.home.routeName })
}

// LIFECYCLE HOOKS
onMounted(async () => {
  if (userStore.isCoachingHasEngaged) await goToHome()

  if (!coachingStore.hasAssignedCoach || data.assignedCoach?.id !== (import.meta.env.VITE_ENV === "prod" ? 22 : 18))
    await assignJulieAsCoach()
  data.showPage = true
})
</script>

<style lang="scss">
.i-coaching-upsell {
  font-family: "DM Sans", system-ui, sans-serif;
  background: var(--grad-bg);
  background-attachment: fixed;
  color: var(--text-body);
  font-size: 16px;
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  scroll-behavior: smooth;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  // ── LAYOUT ────────────────────────────────────────────
  .container {
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .container-wide {
    max-width: 1040px;
    margin: 0 auto;
    padding: 0 20px;
  }

  // ── GRID HELPERS ──────────────────────────────────────
  .grid-2 {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;

    @include respond-to(680px) {
      grid-template-columns: 1fr 1fr;
      gap: 40px;
    }
  }

  .grid-3 {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;

    @include respond-to(680px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 24px;
    }
  }

  // ── SECTION ───────────────────────────────────────────
  section {
    padding: 48px 0;

    @include respond-to(680px) {
      padding: 72px 0;
    }

    & + section {
      border-top: 1px solid var(--border);
    }
  }

  // ── SECTION HELPERS ───────────────────────────────────
  .label {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--purple);
    display: inline-flex;
    align-items: center;
    gap: 8px;

    &::before {
      content: "";
      width: 16px;
      height: 2px;
      background: var(--grad-primary);
      border-radius: 2px;
    }
  }

  .headline {
    font-size: clamp(24px, 4vw, 38px);
    font-weight: 800;
    line-height: 1.18;
    color: var(--text);
    letter-spacing: -0.02em;
  }

  .body-lg {
    font-size: 1.05rem;
    line-height: 1.7;
  }

  .body-sm {
    font-size: 0.875rem;
    color: var(--muted);
    line-height: 1.6;
  }

  .mb-3 {
    margin-bottom: 12px;
  }

  .mt-8 {
    margin-top: 32px;
  }

  .text-center {
    text-align: center;
  }

  // ── SHARED BUTTONS ────────────────────────────────────
  .btn-inline {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 22px;
    background: var(--grad-primary);
    color: white;
    font-size: 14px;
    font-weight: 600;
    border: none;
    border-radius: var(--radius-sm);
    cursor: pointer;
    text-decoration: none;
    transition:
      transform 0.15s,
      box-shadow 0.15s;
    white-space: nowrap;
    flex-shrink: 0;
    box-shadow: 0 4px 16px #6b5cff40;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 24px #6b5cff59;
    }
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: var(--grad-primary);
    color: white;
    border: none;
    border-radius: var(--radius-pill);
    padding: 18px 40px;
    font-size: 1.1rem;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
    transition:
      transform 0.15s,
      box-shadow 0.15s;
    line-height: 1;
    box-shadow: 0 8px 32px #6b5cff59;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 40px #6b5cff73;
    }
  }

  // ── INLINE CTA (shared across sections) ──────────────
  .inline-cta {
    margin-top: 36px;
    padding: 24px;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    box-shadow: var(--card-shadow);

    &-text {
      font-size: 15px;
      color: var(--text-body);
      line-height: 1.5;
      flex: 1;
      min-width: 200px;

      strong {
        color: var(--text);
      }
    }
  }

  .skip {
    margin-top: 12px;
    text-decoration: underline;
    color: #c7c7c7;
    text-align: center;
    font-size: 0.78rem;

    &:hover {
      cursor: pointer;
      color: #878787;
    }
  }

  .inline-proof {
    font-size: 12px;
    color: var(--muted);
    margin-top: 8px;
  }

  // ── SCARCITY / NO-UPSELL LINES ────────────────────────
  .scarcity-line {
    text-align: center;
    font-size: 0.78rem;
    margin-top: 12px;
    color: var(--text-body);

    strong {
      color: var(--red);
    }
  }

  .no-upsell-line {
    text-align: center;
    font-size: 0.75rem;
    margin-top: 8px;
    color: var(--muted);
  }

  // ── HERO ANIMATION ────────────────────────────────────
  @keyframes hero-fade-up {
    from {
      opacity: 0;
      transform: translateY(16px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .hero-anim > * {
    animation: hero-fade-up 0.5s ease both;

    &:nth-child(1) {
      animation-delay: 0.05s;
    }

    &:nth-child(2) {
      animation-delay: 0.12s;
    }

    &:nth-child(3) {
      animation-delay: 0.18s;
    }

    &:nth-child(4) {
      animation-delay: 0.24s;
    }

    &:nth-child(5) {
      animation-delay: 0.3s;
    }

    &:nth-child(6) {
      animation-delay: 0.35s;
    }

    &:nth-child(7) {
      animation-delay: 0.4s;
    }
  }
}
</style>
